import { nextTick, ref, watch, type ComputedRef, type Ref } from 'vue';

// Open choreography: the target element (the detector svg) flies out of the
// clicked button — starting tiny, centered on the button — and collapses back
// into it on close. The caller styles the two phases via the returned flags and
// the --grow-* CSS vars, and reports the CSS animation's end via onAnimationEnd.
export function useZoomFromIcon(
  target: Ref<SVGSVGElement | undefined> | ComputedRef<SVGSVGElement | undefined>,
) {
  const isOpen = ref(false);
  const iconCenter = ref({ x: 0, y: 0 });
  const iconSize = ref(36);
  // translate to the button center plus the scale that shrinks the target to
  // exactly the icon's size (px / factor, set as CSS vars)
  const growOffset = ref({ x: 0, y: 0, scale: 0.04 });
  const isZoomReady = ref(false);
  const isRevealed = ref(false);
  const isClosing = ref(false);

  function openFromIcon(event: MouseEvent) {
    // measure the icon itself (not the padded button) so the graphic shrinks
    // to exactly the icon's footprint
    const button = event.currentTarget as HTMLElement;
    const rect =
      button.querySelector('.v-icon')?.getBoundingClientRect() ?? button.getBoundingClientRect();
    iconCenter.value = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    iconSize.value = rect.width;
    isZoomReady.value = false;
    isRevealed.value = false;
    isClosing.value = false;
    isOpen.value = true;
  }

  watch(isOpen, async (open) => {
    if (!open) return;
    await nextTick();
    const rect = target.value?.getBoundingClientRect();
    if (rect) {
      growOffset.value = {
        x: iconCenter.value.x - (rect.left + rect.width / 2),
        y: iconCenter.value.y - (rect.top + rect.height / 2),
        scale: iconSize.value / rect.width,
      };
    }
    isZoomReady.value = true;
  });

  // Phase 2 trigger (open) / final teardown (close), fired when the target's
  // scale animation finishes.
  function onAnimationEnd() {
    if (isClosing.value) {
      finishClose();
    } else {
      isRevealed.value = true;
    }
  }

  // Close in two steps: first play the zoom-back-to-icon animation,
  // then actually remove the dialog once it finishes.
  function requestClose() {
    if (isClosing.value) return;
    isClosing.value = true;
  }

  function finishClose() {
    if (!isClosing.value) return;
    // Keep isClosing (and its collapsed end state) until the dialog has fully
    // unmounted — resetting it here would re-apply the opening animation during
    // the fade-out. openFromIcon resets the flags on the next open.
    isZoomReady.value = false;
    isOpen.value = false;
  }

  function onDialogModelUpdate(open: boolean) {
    // ESC / backdrop click also go through the zoom-back animation
    if (open) {
      isOpen.value = true;
    } else {
      requestClose();
    }
  }

  return {
    isOpen,
    growOffset,
    isZoomReady,
    isRevealed,
    isClosing,
    openFromIcon,
    onAnimationEnd,
    requestClose,
    onDialogModelUpdate,
  };
}
